import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import { Stack, ControlledInput } from '@ui-components/neo.tax';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useDomainRedirect } from '@neo.tax/onboarding/hooks';
import { DomainRedirectInterstitial, SubmitButton } from '@neo.tax/onboarding/organisms';
import {
  getPathUrl,
  Path,
  useAnalyticId,
  validateEmailAddress,
  SendResetPasswordEmailMutation,
  SendResetPasswordEmailMutationVariables,
  SEND_RESET_PASSWORD_EMAIL,
} from '@neo.tax/shared';

import { ResetPasswordLinkFormValues } from './reset-password.types';

export const ResetPasswordLinkForm = (): React.ReactElement => {
  const { control, formState, handleSubmit, watch } = useForm<ResetPasswordLinkFormValues>({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const emailInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_EMAIL_INPUT);

  const [sendResetPasswordEmail] = useMutation<SendResetPasswordEmailMutation, SendResetPasswordEmailMutationVariables>(
    SEND_RESET_PASSWORD_EMAIL,
  );
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const { isRedirecting, redirectIfSingleTenant } = useDomainRedirect();

  const [email] = watch(['email']);
  const trimmedEmail = email.trim().toLowerCase();

  const onSubmit = async (): Promise<void> => {
    if (await redirectIfSingleTenant(trimmedEmail, { forwardEmail: true })) {
      return;
    }
    setIsSubmitted(true);
    try {
      await sendResetPasswordEmail({
        variables: {
          sendResetPasswordEmailInput: {
            email: trimmedEmail,
            redirectUrl: `${getPathUrl({ path: Path.RESET_PASSWORD })}`,
          },
        },
      });
      // No resetting isSubmitted back to false because we don't want the user to send another email
    } catch {
      setIsSubmitted(false);
    }
  };

  if (isRedirecting) {
    return <DomainRedirectInterstitial />;
  }

  return (
    <Stack as="form" direction="column" paddingY={6} onSubmit={handleSubmit(onSubmit)}>
      <ControlledInput
        control={control}
        name="email"
        rules={{ required: true, validate: validateEmailAddress }}
        id={emailInputId}
        label="Work Email Address"
        placeholder="Work Email Address"
      />
      <SubmitButton
        disabled={!formState.isValid || isSubmitted}
        variant="primary"
        rounded="regular"
        size="base"
        type="submit"
      >
        {isSubmitted ? 'Link Sent' : 'Reset Password'}
      </SubmitButton>
    </Stack>
  );
};
